import '../../../ui-styles/ibest-style.css';
import { useMemo } from 'react';
import { ORIENTATION_TYPES, defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useLivePlotPrintImages, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyze } from './api';
import type { ChartKey, SteelPlateResultState } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, LIVE_PLOT_CARD_MIN_HEIGHT, LIVE_PLOT_SIZE_BY_KEY, PRINT_CHART_ASPECT, PRINT_CHART_IMAGE_OPTIONS, PRINT_CHART_ORDER, SUPPORT_TYPES_STEEL_PLATE } from './appConfig';

import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { ProjectInput, PrintPortal, FormFields, GenerateLinkButton, LivePlot, PrintChartImageGrid, RichSummaryReport } from '../shared';
import { generateShareableURL } from '../shared/urlUtils';

export default function App() {
  // Check for calcId first (short URL from CalcPad)
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  // Merge URL parameters with default values
  const initialValues = useMemo<FormValues>(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'iSteelPlate',
          Lx: '4',
          Ly: '4',
          tpl: '0.25',
          addedWeight: '0',
          orientation: 0,
          support: 1,
          fdy: '1.1*1.2*50000',
          fdu: '1.1*65000',
          E: '29000000',
          density: '490',
          damping: '0.02',
          pressureHistory: '4, 0,0',
          timeHistory: '0, 14, 30',
          u0: '0.0',
          v0: '0.0'
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  // Initialize form with react-hook-form + Zod
  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({
    schema, // Type assertion to handle coercion differences
    defaultValues: initialValues
  });

  // Watch form values for dependent logic
  const values = watch() as FormValues;

  const { saveDataToDB } = useAppStorage<FormValues, SteelPlateResultState>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, SteelPlateResultState>({
    errors,
    values,
    schema,
    analyze,
    saveDataToDB
  });
  const { chartRefs, printRootRef } = useLivePlotPrintImages<ChartKey, SteelPlateResultState>({
    analysisResult,
    setAnalysisResult,
    getImageOptions: key => PRINT_CHART_IMAGE_OPTIONS[PRINT_CHART_ASPECT[key]],
    errorMessage: 'Unable to prepare steel plate charts for printing. Please wait for the charts to finish rendering and try again.',
    enableScreenshot: true
  });
  // Load full state from IndexedDB if calcId is present
  useSavedCalculationLoader<FormValues, SteelPlateResultState>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });

  /* prettier-ignore */
  const fields = defineFields<FormValues>([
    {kind: 'input', name: 'title', label: 'Title', helpText: 'Enter the title of the component', placeholder: 'Component Title' },
    {kind: 'input', name: 'Lx', label: 'Lx', unit: 'ft', helpText: 'Enter the length in X direction', placeholder: 'Lx' },
    {kind: 'input', name: 'Ly', label: 'Ly', unit: 'ft', helpText: 'Enter the length in Y direction', placeholder: 'Ly'},
    {kind: 'input', name: 'tpl', label: 'Plate Thickness', unit: 'in', helpText: 'Enter the plate thickness', placeholder: 'Plate Thickness'},
    {kind: 'input', name: 'addedWeight', label: 'Added Weight', unit: 'psf', helpText: 'Enter the added weight on the component', placeholder: 'Component Added Weight'},
    {kind: 'dropdown', name: 'orientation', label: 'Orientation', options: ORIENTATION_TYPES, helpText: 'Select the orientation of the plate', placeholder: 'Orientation' },
    {kind: 'dropdown', name: 'support', label: 'Support', options: SUPPORT_TYPES_STEEL_PLATE, helpText: 'Enter the support type...'},
    {kind: 'input', name: 'fdy', label: (<span>F<sub>dy</sub></span>), unit: 'psi', helpText: 'Dynamic yield strength Fdy', placeholder: 'Dynamic yield strength Fdy'},
    {kind: 'input', name: 'fdu', label: (<span>F<sub>du</sub></span>), unit: 'psi', helpText: 'Ultimate Strength',placeholder: 'Ultimate Strength'},
    {kind: 'input', name: 'E', label: 'Modulus ', unit: 'psi', helpText: 'Modulus of Elasticity', placeholder: 'Modulus of Elasticity'},
    {kind: 'input', name: 'density', label: 'Density', unit: 'pcf', helpText: 'Density of CMU', placeholder: 'Density of CMU' },
    {kind: 'input', name: 'damping', label: 'Damping', helpText: 'Solver damping (2% typical for steel)', placeholder: 'Damping' },
    { kind: 'input', name: 'pressureHistory', label: 'Pressure History', unit: 'psi', helpText: 'Pressure History', placeholder: 'Pressure History' },
    { kind: 'input', name: 'timeHistory', label: 'Time History', unit: 'msec', helpText: 'Time History', placeholder: 'Time History' },
    { kind: 'input', name: 'u0', label: 'Initial Displacement', unit: 'in', helpText: 'Initial Displacement', placeholder: 'Initial Displacement' },
    { kind: 'input', name: 'v0', label: 'Initial Velocity', unit: 'in/msec', helpText: 'Initial Velocity', placeholder: 'Initial Velocity' }
  ]);

  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">Steel plate analysis</p>
          </div>
        </div>
        <div className="analysis-app__controls flex flex-col rounded-xl p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />

          <div className="analysis-app__actions flex w-full flex-col gap-2 mt-2 sm:mt-1 sm:flex-row sm:flex-wrap">
            <button className="submit-button w-full sm:w-auto sm:flex-1" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button className="submit-button w-full sm:w-auto sm:flex-1" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} className="w-full sm:w-auto" />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid min-w-0 grid-rows-[auto_1fr] rounded-xl gap-y-0 w-full">
          <div className="analysis-app__results shadow-div rounded-xl m-1 min-w-0 overflow-hidden">
            {analysisResult?.summary ? <RichSummaryReport summary={analysisResult.summary} className="rounded-lg border bg-white p-4 text-[13px] leading-5 text-slate-800" /> : <div className="p-4 text-black-500 italic">{analysisResult ? 'Saved result does not include a summary. Re-run the analysis to generate it.' : 'No analysis results yet.'}</div>}
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.plotData && (
              <div className="analysis-app__charts-grid grid grid-cols-1 xl:grid-cols-2 items-stretch gap-3 m-2 sm:m-4 print:gap-1">
                {Object.entries(analysisResult.plotData)
                  .filter(([, plot]) => !!plot)
                  .map(([key, plot]) => {
                    const chartKey = key as ChartKey;

                    return (
                      <div key={key} className="analysis-app__chart-card flex min-w-0 items-center justify-center break-inside-avoid" style={{ minHeight: LIVE_PLOT_CARD_MIN_HEIGHT }}>
                        <LivePlot
                          ref={node => {
                            chartRefs.current[chartKey] = node;
                          }}
                          plot={plot}
                          {...LIVE_PLOT_SIZE_BY_KEY[chartKey]}
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
        <div className="print:hidden hidden lg:block"></div>
      </div>

      {/* ---- PRINT PORTAL ---- */}
      <PrintPortal>
        <div ref={printRootRef} className="print-root">
          <div className="print-a4-landscape box-border bg-white p-[1mm]">
            <div className="print-page-content hidden print:grid h-full min-h-0 grid-cols-[24%_76%] border border-black print:bg-white print:text-black">
              <div className="flex flex-col h-full min-h-0">
                <div className="p-1">
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none">{values.title || 'iSteel Plate'}</div>
                  <div className="text-[10px] opacity-70 m-0 p-0 leading-none mb-1">Project: {values.project || '—'}</div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    <RichSummaryReport summary={analysisResult?.summary} className="text-[9.5px] leading-[1.18] text-black" />
                  </div>
                </div>
                <div className="grid grid-cols-2 mt-auto p-1 text-[9px] border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-blue-600 hover:underline">
                    🔗 LINK
                  </a>
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none text-blue-500">{`${APP_NAME} V${APP_VERSION}`}</div>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="h-full min-h-0 overflow-hidden">
                  <PrintChartImageGrid charts={analysisResult?.charts} order={PRINT_CHART_ORDER} aspectByKey={PRINT_CHART_ASPECT} fitPage />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
